import { Shape } from '../types/Shape';

/**
 * Blob 객체를 Base64 문자열로 변환합니다.
 */
export const blobToBase64 = (blob: Blob): Promise<string> => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
            resolve(reader.result as string);
        };
        reader.onerror = reject;
        reader.readAsDataURL(blob);
    });
};

/**
 * Base64 문자열을 Blob 객체로 변환합니다.
 */
export const base64ToBlob = (base64: string, mimeType: string): Blob => {
    const byteString = atob(base64.split(',')[1]);
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);

    for (let i = 0; i < byteString.length; i++) {
        ia[i] = byteString.charCodeAt(i);
    }

    return new Blob([ab], { type: mimeType });
};

/**
 * 캔버스 데이터를 JSON 파일로 내보냅니다.
 * 이미지와 PDF 파일은 Base64로 인코딩되어 포함됩니다.
 */
export const exportData = async (shapes: Shape[]) => {
    try {
        const shapesToSave = await Promise.all(
            shapes.map(async (shape) => {
                const newShape = { ...shape };

                // 이미지 처리
                if (newShape.type === 'image' && newShape.imageDataUrl) {
                    // 이미 imageDataUrl이 Base64 형태라면 그대로 사용, Blob URL이라면 변환 필요
                    // 현재 구조상 imageDataUrl은 보통 Base64로 저장되지만, 만약 Blob URL을 사용하는 경우가 있다면 처리가 필요함
                    // 여기서는 이미 Base64라고 가정하거나, 필요시 fetch해서 변환하는 로직 추가 가능
                    // notesense 구조상 imageDataUrl은 보통 Base64임.
                }

                // PDF 처리
                if (newShape.type === 'pdf' && newShape.pdfFile) {
                    try {
                        const base64 = await blobToBase64(newShape.pdfFile);
                        newShape._pdfBase64 = base64;
                        // File 객체는 직렬화되지 않으므로 제거할 필요는 없지만 저장 안됨.
                    } catch (e) {
                        console.error('PDF file conversion failed:', e);
                    }
                }

                // 렌더링된 이미지 캐시 등 불필요한 데이터 제거 (선택사항)
                if (newShape.pageImages) {
                    delete newShape.pageImages;
                }

                return newShape;
            })
        );

        const dataStr = JSON.stringify(shapesToSave, null, 2);
        const blob = new Blob([dataStr], { type: 'application/json' });
        const url = URL.createObjectURL(blob);

        const a = document.createElement('a');
        a.href = url;
        a.download = `notesense-data-${new Date().toISOString().slice(0, 10)}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);

    } catch (error) {
        console.error('Export failed:', error);
        throw new Error('저장 중 오류가 발생했습니다.');
    }
};

/**
 * JSON 파일에서 캔버스 데이터를 불러옵니다.
 */
export const importData = async (file: File): Promise<Shape[]> => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = async (e) => {
            try {
                const json = e.target?.result as string;
                const shapes = JSON.parse(json) as Shape[];

                const restoredShapes = await Promise.all(
                    shapes.map(async (shape) => {
                        // PDF 복원
                        if (shape.type === 'pdf' && shape._pdfBase64) {
                            try {
                                const blob = base64ToBlob(shape._pdfBase64, 'application/pdf');
                                const file = new File([blob], "restored.pdf", { type: 'application/pdf' });

                                shape.pdfFile = file;
                                shape.pdfUrl = URL.createObjectURL(file);
                                delete shape._pdfBase64; // 메모리 절약
                            } catch (err) {
                                console.error('PDF restoration failed:', err);
                            }
                        }
                        return shape;
                    })
                );

                resolve(restoredShapes);
            } catch (error) {
                console.error('Import failed:', error);
                reject(error);
            }
        };

        reader.onerror = reject;
        reader.readAsText(file);
    });
};
